import React, { useRef, useState, useEffect } from "react";
import { Button, Select, Input, Form, Upload, message, Image } from 'antd'
import { useLockFn } from "ahooks";
import { globalPrompt } from "@src/components/YhMessage";
import { FormItemTypeEnum, ISelectOption, IYhForm, IYhFormRef } from "@components/YhFormBox/Types";
import { YhForm } from "@components/YhFormBox";
import { YhBox } from "@src/components/YhBox";
import { useNavModel, RouterParamType } from "@src/model/navModel";
import { rosterDetailsModels } from "./rosterDetailsModels";
import { userService } from "../../../user/UserService";
import { getBaseImgUploadUrl, getBaseUploadData, getBaseImgLookUrl } from '@src/utils/constants';
import { UploadOutlined } from '@ant-design/icons';
import { forFind } from "@src/utils/utils";
import useGlobalModel from "@src/model/globalModel";
import defaultAvatar from '@src/assets/images/default-avatar.png';
import moment from "moment";

export const BasicInfo: React.FC = () => {
  const { user } = useGlobalModel()
  const { getSelectedNavState, closeNav, selectedNavId } = useNavModel();
  const [detailsInfo] = useState<RouterParamType>(getSelectedNavState() as RouterParamType);
  const { id, mode } = detailsInfo;
  const {
    loadDs, getDetail,
    getFormInfo,
    onCancel,
    baseTypeDs, userTypeDs, orgDs, storeDs, roleDs,
  } = rosterDetailsModels();

  const [displayAvatarUrl, setDisplayAvatarUrl] = useState(""); // 用于展示的头像URL
  const [saveAvatarName, setSaveAvatarName] = useState(""); // 用于保存的头像文件名

  useEffect(() => {
    loadDs("baseInfo");
    if (mode === "edit") {
      getDetail(id, mode);
    }
  }, [detailsInfo]);

  useEffect(() => {
    if (getFormInfo(mode)?.birthDate) {
      const age = calculateAge(getFormInfo(mode).birthDate);
      formRef.current?.setFieldsValue({ age });
    }
    if (getFormInfo(mode)?.avatar) {
      const avatar = getFormInfo(mode)?.avatar;
      setDisplayAvatarUrl(getBaseImgLookUrl(avatar));
      setSaveAvatarName(avatar);
    }
    else {
      setDisplayAvatarUrl("");
      setSaveAvatarName("");
    }
  }, [getFormInfo(mode)]);

  const formRef = useRef<IYhFormRef>();

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return '';
    const today = moment();
    const birth = moment(birthDate);
    return today.diff(birth, 'years').toString();
  };

  const handleBirthDateChange = (date: any) => {
    if (date) {
      const age = calculateAge(date.format('YYYY-MM-DD'));
      formRef.current?.setFieldsValue({ age });
    } else {
      formRef.current?.setFieldsValue({ age: '' });
    }
  };

  const formConfig: IYhForm = {
    formRef,
    labelCol: { span: 9 },
    disabled: false,
    formValues: getFormInfo(mode),
    justify: 'start',
    items: [
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "登录账号",
        fieldName: "code",
        span: 6,
        rules: [{ required: true, message: '该项为必填项' }],
        disable: mode === 'edit'
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "姓名",
        fieldName: "name",
        span: 6,
        rules: [{ required: true, message: '该项为必填项' }],
      },
      {
        type: FormItemTypeEnum.Select,
        fieldLabel: "用户类型",
        fieldName: "userTypeCode",
        span: 6,
        rules: [{ required: true, message: '该项为必填项' }],
        ds: userTypeDs,
      },
      {
        type: FormItemTypeEnum.Datepicker,
        format: "YYYY-MM-DD",
        fieldLabel: "出生日期",
        fieldName: "birthDate",
        span: 6,
        onChange: handleBirthDateChange
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "年龄",
        fieldName: "age",
        span: 6,
        disable: true,
      },
      {
        type: FormItemTypeEnum.TreeSelect,
        fieldLabel: "所属机构",
        fieldName: "orgCode",
        span: 6,
        rules: [{ required: true, message: "请选择所属机构" }],
        treeDs: orgDs,
        filterOption: (inputValue: string, option: ISelectOption) => option.label.includes(inputValue),
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "员工工号",
        fieldName: "staffId",
        span: 6,
        rules: [{ required: true, message: '该项为必填项' }],
      },
      {
        type: FormItemTypeEnum.Datepicker,
        format: "YYYY-MM-DD",
        fieldLabel: "入职日期",
        fieldName: "hireDate",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "岗位",
        fieldName: "job",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "联系电话",
        fieldName: "phoneNumber",
        span: 6,
        rules: [{ required: true, message: '该项为必填项' }],
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "联系电话2",
        fieldName: "phoneNumber1",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Select,
        fieldLabel: "性别",
        fieldName: "gender",
        span: 6,
        ds: [{ value: 1, label: '男' }, { value: 2, label: '女' }]
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "民族",
        fieldName: "ethnicity",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "邮箱地址",
        fieldName: "email",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "微信账号",
        fieldName: "weChatAccount",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Select,
        fieldLabel: "就职状态",
        fieldName: "employmentStatus",
        span: 6,
        ds: [{ value: "1", label: '在职' }, { value: "0", label: '离职' }]
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "政治面貌",
        fieldName: "politicalStatus",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "联系地址",
        fieldName: "address",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "入职渠道",
        fieldName: "hireChannel",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Datepicker,
        format: "YYYY-MM-DD",
        fieldLabel: "离职时间",
        fieldName: "terminationDate",
        span: 6,
      },
      {
        type: FormItemTypeEnum.Other,
        fieldLabel: '是否住宿',
        fieldName: 'accommodation',
        span: 6,
        formItem: (
          <div className="accommodationStyle">
            <div style={{ display: 'flex' }}>
              <span style={{ marginLeft: 53 }}>是否住宿：</span>
              <Form.Item name="accommodation">
                <Select style={{ width: 60 }}>
                  <Select.Option value="0">否</Select.Option>
                  <Select.Option value="1">是</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="dormitoryNumber">
                <Input placeholder={"宿舍"} style={{ width: 93, marginLeft: 2, height: 32 }} addonAfter="宿舍" className="input-align-middle" />
              </Form.Item>
              <Form.Item name="dormitoryFee">
                <Input placeholder={"费用"} style={{ width: 60, marginLeft: 2, height: 32 }} addonAfter="¥" className="input-align-middle" />
              </Form.Item>
            </div>
          </div>
        ),
      },
      {
        type: FormItemTypeEnum.Select,
        fieldLabel: "办公基地",
        fieldName: "dormitory",
        span: 6,
        ds: baseTypeDs,
        placeholder: "请选择办公基地"
      },
      {
        type: FormItemTypeEnum.Select,
        fieldLabel: "部门负责人",
        fieldName: "departmentLeader",
        span: 6,
        ds: [
          { label: "是", value: 1 },
          { label: "否", value: 0 }
        ]
      },
      {
        type: FormItemTypeEnum.Switch,
        fieldLabel: "状态",
        fieldName: "status",
        span: 6,
        checkedChildren: "启用",
        unCheckedChildren: "禁用",
        defaultChecked: true
      },
      {
        type: FormItemTypeEnum.Select,
        mode: "multiple",
        fieldLabel: "所属门店",
        fieldName: "shops",
        span: 6,
        ds: storeDs,
        filterOption: (inputValue: string, option: ISelectOption) => option.label.includes(inputValue),
      },
      {
        type: FormItemTypeEnum.Select,
        mode: "multiple",
        fieldLabel: "所属角色",
        fieldName: "roles",
        span: 6,
        ds: roleDs,
        filterOption: (inputValue: string, option: ISelectOption) => option.label.includes(inputValue),
      },
      {
        type: FormItemTypeEnum.Input,
        fieldLabel: "图像",
        fieldName: "avatar",
        span: 6,
        hidden: true,
      },
    ]
  };

  const onSave = useLockFn(async () => {
    try {
      const formData = await formRef.current?.validateFields();
      if (formData) {
        const params = {
          ...formData,
          staffId: formData.staffId?.trim(),
          birthDate: formData.birthDate ? moment(formData.birthDate).format("YYYY-MM-DD") : null,
          hireDate: formData.hireDate ? moment(formData.hireDate).format("YYYY-MM-DD") : undefined,
          terminationDate: formData.terminationDate ? moment(formData.terminationDate).format("YYYY-MM-DD") : null,
          status: formData.status ? 0 : 1,
          userTypeName: userTypeDs?.find(item => item.value === formData.userTypeCode)?.label ?? "",
          orgName: forFind(orgDs ?? [], formData.orgCode as string, "value")?.title ?? "",
          gen: formData.status ? 0 : 1,
          gender: formData.gender ?? 1,
          shops: formData.shops ?? [],
          avatar: saveAvatarName, // 使用保存的头像文件名
        }
        if (mode !== "edit") {
          const insertRet = await userService.insert({ ...params, createUser: user.userName });
          if (insertRet.success) {
            await globalPrompt("message", { type: "success", text: '新增成功!' });
            closeNav(selectedNavId);
          } else {
            await globalPrompt("message", { type: "error", text: "新增失败!" + insertRet.retMsg });
          }
        } else {
          const updateRet = await userService.updateLocal(params);
          if (updateRet.success) {
            await globalPrompt("message", { type: "success", text: '修改成功!' });
            getDetail(id, mode);
          } else {
            await globalPrompt("message", { type: "error", text: "修改失败!" + updateRet.retMsg });
          }
        }
      }
    } catch (e) {
      console.log("失败!");
    }
  })

  const action = (
    <>
      <Button className="mr-5" type="link" shape="round" onClick={() => { onSave() }}>保存</Button>
      <Button className="mr-5" type="link" shape="round" onClick={() => { onCancel(mode) }}>取消</Button>
    </>
  );

  const getSeniority = () => {
    let seniority = '';
    if (getFormInfo(mode)?.hireDate) {
      const hire = moment(getFormInfo(mode)?.hireDate, 'YYYY-MM-DD');
      const now = moment();
      const years = now.diff(hire, 'years');
      const months = now.diff(hire.add(years, 'years'), 'months');

      if (years < 1) {
        seniority = months + '个月';
      } else {
        seniority = years + '年' + (months > 0 ? months + '个月' : '');
      }
    }
    return seniority;
  }

  // 头像上传处理
  const handleAvatarChange = (info: any) => {
    if (info.file.status === 'done') {
      const url = info.file.response?.retData || '';
      // 从完整URL中提取文件名
      const fileName = url.split('/').pop()?.split('?')[0] || '';
      console.log('上传头像返回图片地址:', url);
      setDisplayAvatarUrl(getBaseImgLookUrl(url));
      setSaveAvatarName(fileName);
      formRef.current?.setFieldsValue({ avatar: fileName });
      message.success('头像上传成功');
    }
  };

  return (
    <YhBox action={action} style={{ overflowY: 'auto', padding: '10px 10px 100px 10px', margin: 10 }}>
      {mode === "edit" &&
        <div style={{ width: "100%", height: 115, background: "#E6EEF9", display: "flex", alignItems: "center", padding: "0 20px", marginTop: 10 }}>
          <Form.Item name="avatar" valuePropName="avatar" style={{ marginLeft: 20, marginBottom: 0 }}>
            <div style={{ position: 'relative', width: 90, height: 90 }}>
              <Image
                src={displayAvatarUrl || defaultAvatar}
                alt="用户头像"
                style={{ width: 90, height: 90, borderRadius: '50%', cursor: 'pointer' }}
                preview={{
                  mask: '点击预览',
                  maskClassName: 'custom-image-mask'
                }}
              />
              <Upload
                name="file"
                showUploadList={false}
                action={getBaseImgUploadUrl()}
                data={getBaseUploadData()}
                onChange={handleAvatarChange}
                accept="image/*"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<UploadOutlined />}
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    padding: 0,
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Upload>
            </div>
          </Form.Item>
          <div style={{ marginLeft: 20, color: "#333" }}>
            <div style={{ fontSize: 16, marginBottom: 8 }}>{getFormInfo(mode)?.name || ''}</div>
            <div style={{ fontSize: 14, color: "#666" }}>
              <span>部门：{getFormInfo(mode)?.orgName || '-'}</span>
              <span style={{ marginLeft: 50 }}>职位：{getFormInfo(mode)?.job || '-'}</span>
              <span style={{ marginLeft: 50 }}>入职日期：{
                getFormInfo(mode)?.hireDate
                  ? (typeof getFormInfo(mode)?.hireDate === 'string'
                    ? getFormInfo(mode)?.hireDate
                    : moment(getFormInfo(mode)?.hireDate).format('YYYY-MM-DD'))
                  : '-'
              }</span>
              <span style={{ marginLeft: 50 }}>司龄：{getSeniority() || '-'}</span>
            </div>
          </div>
        </div>
      }
      <div style={{ marginBottom: 10, padding: 10, fontSize: 18, fontWeight: "bold" }}>基本信息</div>
      <YhForm  {...formConfig} />
    </YhBox>
  );
}